import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { EMAIL_IS_INVALID, FIELD_IS_REQUIRED, PHONE_IS_INVALID } from 'const'
import { EPartnerCodeStatus, EPartnerCodeType, Nullish, PartnerCode } from 'types'
import { nativeEnum, object, string, TypeOf } from 'zod'

// Same wording the backend answers with when the count is missing or invalid.
const LICENSES_ARE_INVALID = 'Number of Licenses must be a positive number'

const formSchema = object({
  codeType: nativeEnum(EPartnerCodeType),
  status: nativeEnum(EPartnerCodeStatus),
  organizationName: string().min(1, FIELD_IS_REQUIRED),
  // Raw input: the field exists for funded codes only, so it is checked below instead of coerced.
  numberOfUsers: string().optional(),
  contactFirstName: string().optional(),
  contactLastName: string().optional(),
  contactEmail: string().email(EMAIL_IS_INVALID).optional().or(string().length(0)),
  contactPhone: string().min(10, PHONE_IS_INVALID).optional().or(string().length(0)),
  note: string().optional(),
}).superRefine((values, ctx) => {
  // Unfunded codes have no user limit — a license count applies to funded codes only.
  if (values.codeType !== EPartnerCodeType.funded) return

  const count = Number(values.numberOfUsers)
  if (!values.numberOfUsers?.trim() || !Number.isInteger(count) || count < 1) {
    ctx.addIssue({ code: 'custom', path: ['numberOfUsers'], message: LICENSES_ARE_INVALID })
  }
})

export type PartnerCodeFormType = TypeOf<typeof formSchema>

const emptyValues: PartnerCodeFormType = {
  // A new code is unfunded unless the admin picks otherwise, and is always created active.
  codeType: EPartnerCodeType.unfunded,
  status: EPartnerCodeStatus.active,
  organizationName: '',
  numberOfUsers: '',
  contactFirstName: '',
  contactLastName: '',
  contactEmail: '',
  contactPhone: '',
  note: '',
}

const valuesOf = (item: PartnerCode): PartnerCodeFormType => ({
  codeType: item.codeType,
  status: item.status,
  organizationName: item.organizationName,
  numberOfUsers: item.numberOfUsers != null ? String(item.numberOfUsers) : '',
  contactFirstName: item.contactFirstName || '',
  contactLastName: item.contactLastName || '',
  contactEmail: item.contactEmail || '',
  contactPhone: item.contactPhone || '',
  note: item.note || '',
})

export const partnerCodeFormSettings = (editedItem: Nullish<PartnerCode>) => ({
  mode: 'onChange' as const,
  resolver: zodResolver(formSchema),
  defaultValues: editedItem ? valuesOf(editedItem) : emptyValues,
})
